import React, { useState } from "react";
import styled from "styled-components";
import type {
  InterstateOnChangeEvent,
  SelectInputEventValue
} from "@dealer-center/core-components";
import { Alert, Button, TextInput } from "@dealer-center/core-components";
import type {
  LocaleYearMakeData,
  ModelCreateRequest,
  ModelCreateResponse,
  SearchResponse
} from "@dealer-center/inventory-pages-gvm-interface";
import type { UseMutationResult } from "@tanstack/react-query";
import { LocaleYearMakeFilters } from "../Common";
import { useIsGvmRwAccessUser } from "../util/gvmUserUtil";
import { Link } from "react-router-dom";

const StyledSearchForm = styled.div`
  box-shadow: 3px 3px 2px #999;
  border: 1px #ddd solid;
  margin-right: 10px;
  padding: 8px;
  min-width: 745px;
  min-height: 100px;
  max-width: 100%;
  display: grid;
  grid-template-columns: repeat(4, auto);
  grid-template-rows: repeat(2, auto);
  gap: 10px;
`;

const resetInputs = {
  model: undefined,
  bodyStyle: undefined
};

interface ModelCreatorFormProps {
  localeYearMakeSearchResponse: SearchResponse<LocaleYearMakeData>;
  modelCreateRequest: ModelCreateRequest;
  setModelCreateRequest: (newData: ModelCreateRequest) => void;
  createModelMutation: UseMutationResult<
    ModelCreateResponse,
    Error,
    ModelCreateRequest,
    unknown
  >;
}

export const ModelCreatorForm = ({
  localeYearMakeSearchResponse,
  modelCreateRequest,
  setModelCreateRequest,
  createModelMutation
}: ModelCreatorFormProps) => {
  const isGvmRwAccessUser = useIsGvmRwAccessUser();
  const [modelCreateResponse, setModelCreateResponse] =
    useState<ModelCreateResponse>();
  const isReadyToSave =
    !!modelCreateRequest?.locale &&
    !!modelCreateRequest?.year &&
    !!modelCreateRequest?.make &&
    !!modelCreateRequest?.model &&
    modelCreateRequest.model.trim().length > 0 &&
    !!modelCreateRequest?.bodyStyle &&
    modelCreateRequest.bodyStyle.trim().length > 0;

  const isElementDisabled =
    !isGvmRwAccessUser || !isReadyToSave || createModelMutation.isPending;

  const handleInputChange = (
    event: InterstateOnChangeEvent<SelectInputEventValue>
  ) => {
    const name = event.target.name || "";
    const value =
      name === "year" ? Number(event.target.value) : event.target.value;

    setModelCreateRequest({ ...modelCreateRequest, [name]: value });
  };

  const handleCreate = () => {
    createModelMutation.mutate(modelCreateRequest, {
      onSuccess: data => {
        setModelCreateRequest({ locale: "US_EN" });
        setModelCreateResponse(data);
      }
    });
  };

  const renderModelCreatedAlert = (
    modelCreateResponse: ModelCreateResponse
  ) => {
    return (
      <Alert disableSuccessFade={true} type="success">
        <span>
          Model created successfully:{" "}
          <Link
            rel="noopener noreferrer"
            to={`/gvm/vehicle?id=${modelCreateResponse.globalVehicleId}`}
          >
            {`${modelCreateResponse.year} ${modelCreateResponse.make} ${modelCreateResponse.model} ${modelCreateResponse.bodyStyle}`}
          </Link>
        </span>
      </Alert>
    );
  };

  const render = () => {
    return (
      <StyledSearchForm
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gridGap: "10px"
        }}
      >
        {modelCreateResponse && renderModelCreatedAlert(modelCreateResponse)}
        <h2>Model Creation</h2>
        <LocaleYearMakeFilters
          localeYearMakeRequest={modelCreateRequest}
          queryData={localeYearMakeSearchResponse}
          resettableProps={resetInputs}
          setLocaleYearMakeRequest={setModelCreateRequest}
        />
        <TextInput
          maxLength={275}
          name="model"
          placeholder="Model"
          value={modelCreateRequest?.model || ""}
          onChange={handleInputChange}
        />
        <TextInput
          maxLength={150}
          name="bodyStyle"
          placeholder="Body Style"
          value={modelCreateRequest?.bodyStyle || ""}
          onChange={handleInputChange}
        />
        <Button
          analyticsConfiguration={{
            eventName: "Create Model button clicked"
          }}
          disabled={isElementDisabled}
          sx={{ width: "100%", marginTop: "10px" }}
          type="button"
          onClick={handleCreate}
        >
          {createModelMutation.isPending ? "Saving..." : "Create Model"}
        </Button>
      </StyledSearchForm>
    );
  };

  return render();
};
